import React, { useEffect, useMemo, useState } from 'react';
import { IconChevronDown } from './Icons';
import { buildMonthlyRows } from '../utils/vectorCapitalScenario';

/**
 * Fyntrak-style "Key Insights" (+ Key Metrics Comparison) and
 * "Payback / Growth Calculator" panels for the Vector Capital scenario page.
 * Everything is derived from simulated strategy / benchmark equity series.
 *
 * Shared prop shape:
 * - scenarios / benchmarks: [{ id, label, series: [{ date, normalized }] }]
 */

/** Total return %, annualized return % and max drawdown % from a series. */
function computeSeriesInsightStats(series) {
  const rows = Array.isArray(series) ? series : [];
  if (rows.length < 2) return null;
  const start = Number(rows[0]?.normalized);
  const end = Number(rows[rows.length - 1]?.normalized);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0) return null;

  const totalReturnPct = ((end / start) - 1) * 100;

  const startDate = new Date(`${rows[0].date}T00:00:00Z`);
  const endDate = new Date(`${rows[rows.length - 1].date}T00:00:00Z`);
  const years = Math.max((endDate - startDate) / (365.25 * 24 * 60 * 60 * 1000), 0);
  const months = years * 12;
  const annualizedReturnPct = years > 0 ? ((end / start) ** (1 / years) - 1) * 100 : totalReturnPct;

  let peak = -Infinity;
  let maxDrawdownPct = 0;
  rows.forEach((p) => {
    const v = Number(p?.normalized);
    if (!Number.isFinite(v)) return;
    if (v > peak) peak = v;
    if (peak > 0) {
      const dd = ((v - peak) / peak) * 100;
      if (dd < maxDrawdownPct) maxDrawdownPct = dd;
    }
  });

  return { totalReturnPct, annualizedReturnPct, maxDrawdownPct, years, months };
}

function fmtSignedPct(value, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

function CollapsiblePanel({ eyebrow, title, children }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="vector-capital-panel vcc-section-panel">
      <button
        type="button"
        className="vcc-section-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div>
          <span className="vector-capital-panel-eyebrow">{eyebrow}</span>
          <h3>{title}</h3>
        </div>
        <IconChevronDown
          size={18}
          color="rgba(191, 219, 254, 0.9)"
          className={expanded ? 'vcc-section-chevron is-open' : 'vcc-section-chevron'}
        />
      </button>
      {expanded && children}
    </div>
  );
}

/** Key Insights: outperformance / risk / consistency cards + Key Metrics table. */
export function VectorCapitalKeyInsights({
  scenarios,
  benchmarks = [],
  capitalBase = 100000,
  resolveMonthlyFixedBase,
}) {
  const allSeries = useMemo(() => [...scenarios, ...benchmarks], [scenarios, benchmarks]);

  const statsById = useMemo(() => {
    const out = new Map();
    allSeries.forEach((s) => {
      const stats = computeSeriesInsightStats(s.series);
      if (stats) out.set(s.id, stats);
    });
    return out;
  }, [allSeries]);

  const insights = useMemo(() => {
    const outperformance = [];
    const risk = [];
    const consistency = [];

    scenarios.forEach((scenario) => {
      const sStats = statsById.get(scenario.id);
      if (!sStats) return;
      benchmarks.forEach((benchmark) => {
        const bStats = statsById.get(benchmark.id);
        if (!bStats) return;
        const diff = sStats.totalReturnPct - bStats.totalReturnPct;
        outperformance.push({
          key: `${scenario.id}-${benchmark.id}`,
          name: scenario.label,
          text: `has ${diff >= 0 ? 'outperformed' : 'underperformed'} ${benchmark.label} by ${fmtSignedPct(diff)} over the period.`,
        });

        const sDd = Math.abs(sStats.maxDrawdownPct);
        const bDd = Math.abs(bStats.maxDrawdownPct);
        if (bDd > 0.01) {
          const ddDiff = ((bDd - sDd) / bDd) * 100;
          risk.push({
            key: `${scenario.id}-${benchmark.id}`,
            name: scenario.label,
            text: `maximum drawdown is ${Math.abs(ddDiff).toFixed(0)}% ${ddDiff >= 0 ? 'lower' : 'higher'} than ${benchmark.label}.`,
          });
        }
      });

      const monthly = buildMonthlyRows(scenario.series, {
        fixedBaseCapital: typeof resolveMonthlyFixedBase === 'function'
          ? resolveMonthlyFixedBase(scenario.id)
          : capitalBase,
      });
      if (monthly.length) {
        const positive = monthly.filter((m) => (m.pct ?? 0) > 0).length;
        consistency.push({
          key: scenario.id,
          name: scenario.label,
          text: `${positive} out of ${monthly.length} months with positive returns.`,
        });
      }
    });

    return { outperformance, risk, consistency };
  }, [scenarios, benchmarks, statsById, capitalBase, resolveMonthlyFixedBase]);

  if (!allSeries.length) return null;

  const renderItems = (items, emptyText) => (
    items.length
      ? items.map((item) => (
        <p key={item.key}>
          <strong>{item.name}</strong> {item.text}
        </p>
      ))
      : <p className="vci-empty">{emptyText}</p>
  );

  return (
    <CollapsiblePanel eyebrow="Analysis" title="Key Insights">
      <div className="vci-cards">
        <div className="vci-card">
          <h4>Outperformance</h4>
          {renderItems(insights.outperformance, 'Select a strategy and a benchmark to compare.')}
        </div>
        <div className="vci-card">
          <h4>Risk Management</h4>
          {renderItems(insights.risk, 'Select a strategy and a benchmark to compare.')}
        </div>
        <div className="vci-card">
          <h4>Consistency</h4>
          {renderItems(insights.consistency, 'Select a strategy to see consistency.')}
        </div>
      </div>

      <h4 className="vci-subtitle">Key Metrics Comparison</h4>
      <div className="vcc-table-scroll">
        <table className="vcc-table">
          <thead>
            <tr>
              <th>Return Statistics</th>
              {allSeries.map((s) => (
                <th key={s.id} className="vcc-table-center">{s.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="vcc-table-month">Annual Return</td>
              {allSeries.map((s) => {
                const stats = statsById.get(s.id);
                if (!stats) return <td key={s.id} className="vcc-table-num vcc-table-center muted">–</td>;
                const tone = stats.annualizedReturnPct >= 0 ? 'pos' : 'neg';
                return (
                  <td key={s.id} className={`vcc-table-num vcc-table-center ${tone} vcc-cell-tint-${tone}`}>
                    {fmtSignedPct(stats.annualizedReturnPct)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className="vcc-table-month">Max Drawdown</td>
              {allSeries.map((s) => {
                const stats = statsById.get(s.id);
                if (!stats) return <td key={s.id} className="vcc-table-num vcc-table-center muted">–</td>;
                return (
                  <td key={s.id} className="vcc-table-num vcc-table-center neg vcc-cell-tint-neg">
                    {stats.maxDrawdownPct.toFixed(1)}%
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </CollapsiblePanel>
  );
}

/** Payback / Growth Calculator: months to reach a target multiple at the
 *  scenario's geometric average monthly return. */
export function VectorCapitalGrowthCalculator({ scenarios, defaultCapital = 100000 }) {
  const [activeId, setActiveId] = useState(scenarios[0]?.id || '');
  useEffect(() => {
    if (!scenarios.length) return;
    if (!scenarios.some((s) => s.id === activeId)) setActiveId(scenarios[0].id);
  }, [scenarios, activeId]);
  const active = scenarios.find((s) => s.id === activeId) || scenarios[0];

  const [capitalRaw, setCapitalRaw] = useState(String(defaultCapital));
  const [multipleRaw, setMultipleRaw] = useState('2');
  const [result, setResult] = useState(null);

  const avgMonthlyReturnPct = useMemo(() => {
    const stats = computeSeriesInsightStats(active?.series);
    if (!stats || stats.months <= 0) return 0;
    const totalReturn = stats.totalReturnPct / 100;
    if (totalReturn <= -1) return 0;
    return ((1 + totalReturn) ** (1 / stats.months) - 1) * 100;
  }, [active]);

  const handleCalculate = () => {
    const multiple = parseFloat(multipleRaw);
    const monthly = avgMonthlyReturnPct / 100;
    if (!Number.isFinite(multiple) || multiple <= 1 || monthly <= 0) {
      setResult({ error: 'Needs a target multiple above 1× and a strategy with positive average returns.' });
      return;
    }
    const months = Math.ceil(Math.log(multiple) / Math.log(1 + monthly));
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    const capital = parseFloat(String(capitalRaw).replace(/[$,\s]/g, ''));
    setResult({
      months,
      date: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
      target: Number.isFinite(capital) && capital > 0 ? capital * multiple : null,
    });
  };

  if (!active) return null;

  return (
    <CollapsiblePanel eyebrow="Projection" title="Payback / Growth Calculator">
      <div className="vci-calc">
        <div className="vci-calc-inputs">
          <label className="vci-field">
            <span>Strategy</span>
            <select className="vcc-select" value={active.id} onChange={(e) => { setActiveId(e.target.value); setResult(null); }}>
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="vci-field">
            <span>Initial Capital (USD)</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={capitalRaw}
              onChange={(e) => setCapitalRaw(e.target.value)}
            />
          </label>
          <label className="vci-field">
            <span>Target Multiple (× of initial)</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={multipleRaw}
              onChange={(e) => setMultipleRaw(e.target.value)}
            />
          </label>
        </div>
        <div className="vci-calc-output">
          <div className="vci-calc-tile">
            <span>Assumed monthly return</span>
            <strong>{avgMonthlyReturnPct.toFixed(2)}% / month</strong>
            <small>Geometric average from the strategy&apos;s simulated history</small>
          </div>
          <button type="button" className="vci-calc-btn" onClick={handleCalculate}>
            Calculate
          </button>
          {result && (
            result.error ? (
              <div className="vci-calc-tile vci-calc-tile--wide vci-calc-error">{result.error}</div>
            ) : (
              <div className="vci-calc-tile vci-calc-tile--wide vci-calc-result">
                <div>
                  <span>Months to target</span>
                  <strong className="pos">{result.months} mo</strong>
                </div>
                <div>
                  <span>Estimated date reached</span>
                  <strong className="pos">{result.date}</strong>
                </div>
                {result.target !== null && (
                  <div>
                    <span>Target value</span>
                    <strong className="pos">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(result.target)}
                    </strong>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
      <p className="vector-capital-chart-footnote">
        Hypothetical projection from the scenario&apos;s simulated average monthly return — actual results will vary
        and past performance does not guarantee future results.
      </p>
    </CollapsiblePanel>
  );
}
